import {
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  FormControl,
  FormLabel,
  SimpleGrid,
  Stack,
} from '@chakra-ui/react';
import { useFetchUsersPageQuery } from '../../api';
import { useAuth } from '../../core';
import { AppInput } from '../components';
import { DatePicker } from '../form_fields';
import { FormButtonGroup, UserAutoComplete } from '../components';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { PostAfeRequest } from '../../api/bindings/PostAfeRequest';

export interface AfeCreateFormDrawerContentProps
  extends Omit<DrawerContentProps, 'onSubmit'> {
  onSubmit: (form: PostAfeRequest & { includeRevision: boolean }) => void;
  onClose: () => void;
}

interface Form extends PostAfeRequest {
  includeRevision: boolean;
}

export const AfeCreateFormDrawerContent = (
  props: AfeCreateFormDrawerContentProps
) => {
  const { onSubmit, onClose, ...rest } = props;

  // Fetches
  const auth = useAuth();
  const { data: users } = useFetchUsersPageQuery(
    { clientId: auth?.user.clientId ?? '0', all: true },
    { skip: !auth?.user.clientId }
  );

  // State
  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      name: '',
      description: '',
      year: new Date().getFullYear(),
      assigneeUserId: '0',
      budgetStart: null,
      budgetEnd: null,
      estimatedStartDate: null,
      estimatedEndDate: null,
      actualStartDate: null,
      actualEndDate: null,
      includeRevision: false,
    },
  });
  const { control, register, watch, handleSubmit } = form;
  const formName = watch('name');

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DrawerContent {...rest}>
          <DrawerCloseButton />
          <DrawerHeader>Create New AFE: {formName || 'Untitled'}</DrawerHeader>
          <DrawerBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel htmlFor="name">Name:</FormLabel>
                <AppInput
                  id="name"
                  {...register('name', {
                    required: 'Name is required',
                    minLength: {
                      value: 3,
                      message: 'Name must be between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message: 'Name must be between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel htmlFor="description">Description:</FormLabel>
                <AppInput
                  id="description"
                  {...register('description', {
                    required: 'Description is required',
                    minLength: {
                      value: 3,
                      message:
                        'Description must be between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message:
                        'Description must be between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel htmlFor="assignee">Assignee:</FormLabel>
                <Controller
                  control={control}
                  name="assigneeUserId"
                  rules={{
                    required: 'Assignee is required',
                    validate: (value) =>
                      BigInt(value) > 0 || 'Please select an assignee',
                  }}
                  render={(p) => (
                    <UserAutoComplete
                      id="assignee"
                      items={users?.items}
                      {...p.field}
                      onChange={(userId) => p.field.onChange(userId)}
                    />
                  )}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel htmlFor="year">Year:</FormLabel>
                <DatePicker
                  id="year"
                  control={control}
                  name="year"
                  rules={{ required: 'Year is required' }}
                />
              </FormControl>
              <SimpleGrid columns={[1, 2]} gap={[1, 4]}>
                <FormControl>
                  <FormLabel htmlFor="estStart">Estimated Start Date</FormLabel>
                  <DatePicker
                    id="estStart"
                    control={control}
                    name="estimatedStartDate"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="estEnd">Estimated End Date</FormLabel>
                  <DatePicker
                    id="estEnd"
                    control={control}
                    name="estimatedEndDate"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="actualStart">Actual Start</FormLabel>
                  <DatePicker
                    id="actualStart"
                    name="actualStartDate"
                    control={control}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="actualEnd">Actual End</FormLabel>
                  <DatePicker
                    id="actualEnd"
                    name="actualEndDate"
                    control={control}
                  />
                </FormControl>
              </SimpleGrid>
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              onCancel={onClose}
              onConfirm={handleSubmit(onSubmit)}
            />
          </DrawerFooter>
        </DrawerContent>
      </form>
    </FormProvider>
  );
};
